// lead_shop/static/js/combobox_helpers.js
// Utilities for controllers that host combobox_controller.js instances.
// Not a Stimulus controller — imported by any controller that needs to drive comboboxes.

/**
 * Returns the combobox Stimulus controller nested inside wrapperEl, or null.
 * Retries once on the next event-loop tick when Stimulus has not yet connected.
 * @param {Application} application
 * @param {Element|null} wrapperEl
 * @returns {Promise<object|null>}
 */
export async function comboController(application, wrapperEl) {
  if (!wrapperEl) return null
  // querySelector never matches the element itself — handle the case where wrapperEl IS the
  // combobox element (e.g. tests that pass comboEl directly, or a target that is the combobox).
  const comboEl = wrapperEl.matches('[data-controller~="combobox"]')
    ? wrapperEl
    : wrapperEl.querySelector('[data-controller~="combobox"]')
  if (!comboEl) return null
  let ctrl = application.getControllerForElementAndIdentifier(comboEl, "combobox")
  if (!ctrl) {
    await new Promise(res => setTimeout(res, 0))
    ctrl = application.getControllerForElementAndIdentifier(comboEl, "combobox")
  }
  return ctrl ?? null
}

/**
 * Sets the options on a combobox.
 * @param {Application} application
 * @param {Element|null} wrapperEl
 * @param {{value: string|number, label: string, meta?: string, group?: *}[]} options
 */
export async function setComboOptions(application, wrapperEl, options) {
  const ctrl = await comboController(application, wrapperEl)
  if (!ctrl) return
  ctrl.optionsValue = options
}

/**
 * Returns the selected value(s) from a combobox by reading hidden inputs.
 * Single combobox: returns the string value or "".
 * Multiple combobox: returns an array of string values (may be empty).
 * @param {Element|null} wrapperEl
 * @returns {string|string[]}
 */
export function comboValue(wrapperEl) {
  if (!wrapperEl) return ""
  const comboEl = wrapperEl.matches('[data-controller~="combobox"]')
    ? wrapperEl
    : wrapperEl.querySelector('[data-controller~="combobox"]')
  if (!comboEl) return ""
  const multiple = comboEl.dataset.comboboxMultipleValue === "true"
  const name = comboEl.dataset.comboboxNameValue
  if (!name) return multiple ? [] : ""
  const hiddenSpan = comboEl.querySelector('[data-combobox-target="hidden"]')
  if (!hiddenSpan) return multiple ? [] : ""
  const inputs = [...hiddenSpan.querySelectorAll(`input[type="hidden"][name="${name}"]`)]
  if (multiple) return inputs.map(i => i.value).filter(Boolean)
  return inputs[0]?.value ?? ""
}

/**
 * Clears all selected values from a combobox (silently — no combobox:change event).
 * @param {Application} application
 * @param {Element|null} wrapperEl
 */
export async function clearCombo(application, wrapperEl) {
  const ctrl = await comboController(application, wrapperEl)
  if (!ctrl) return
  ctrl.clearAll({ silent: true })
}

/**
 * Selects values on a combobox from outside (e.g. filling an edit form). Local mode: values are
 * looked up in the options already pushed; remote mode: pass {value, label} objects.
 * Silent by default — the caller is restoring state, not reporting a user choice.
 * @param {Application} application
 * @param {Element|null} wrapperEl
 * @param {Array} values
 * @param {{silent?: boolean}} [opts]
 */
export async function selectComboValues(application, wrapperEl, values, { silent = true } = {}) {
  const ctrl = await comboController(application, wrapperEl)
  if (!ctrl) return
  ctrl.selectValues(values, { silent })
}
